import { LeftOutlined, PlusOutlined, RightOutlined } from '@ant-design/icons';
import { PERMISSIONS } from '@navicosa/shared-types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  App,
  Button,
  Card,
  DatePicker,
  Empty,
  Input,
  Modal,
  Select,
  Space,
  Spin,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, errorText } from '../../api/client';
import {
  BOOKING_STATUS_LABEL,
  type ProductListItem,
  type ScheduleBlock,
  type ScheduleData,
} from '../../api/types';
import { useAuth } from '../../auth/AuthContext';

const DAYS = 31;
const BLOCK_STYLE: Record<ScheduleBlock['block_type'], { bg: string; label: string }> = {
  rental: { bg: '#2563EB', label: 'Đơn thuê' },
  maintenance: { bg: '#D97706', label: 'Bảo trì' },
  reserved: { bg: '#7C3AED', label: 'Giữ chỗ' },
  manual: { bg: '#737B86', label: 'Khóa khác' },
};

/** Lịch xe theo ngày (PRD 8.2.H). Mỗi thanh là một khoảng xe bị giữ; trùng lịch bị database chặn. */
export function SchedulePage() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const { message, modal } = App.useApp();
  const { can } = useAuth();
  const canBlock = can(PERMISSIONS.EQUIPMENT_MANAGE);
  const [start, setStart] = useState<Dayjs>(dayjs().startOf('day'));
  const [productId, setProductId] = useState<string>();
  const [adding, setAdding] = useState<{ id: string; code: string } | null>(null);
  const [form, setForm] = useState<{
    range: [Dayjs, Dayjs] | null;
    type: 'maintenance' | 'reserved' | 'manual';
    note: string;
  }>({
    range: null,
    type: 'maintenance',
    note: '',
  });

  const from = start;
  const to = start.add(DAYS, 'day');
  const q = useQuery({
    queryKey: ['schedule', from.toISOString(), productId],
    queryFn: () =>
      api.get<ScheduleData>('/admin/schedule', {
        from: from.toISOString(),
        to: to.toISOString(),
        product_id: productId,
      }),
  });
  const products = useQuery({
    queryKey: ['products-pick'],
    queryFn: async () => (await api.page<ProductListItem>('/admin/products', { limit: 100 })).items,
  });

  const span = to.valueOf() - from.valueOf();
  // Hiển thị theo trọn ngày: đơn trong 1 ngày (7h–17h) vẫn chiếm đủ 1 ô ngày
  const pos = (b: ScheduleBlock) => {
    const start = dayjs(b.start_at).startOf('day');
    const rawEnd = dayjs(b.end_at);
    const end = rawEnd.isSame(rawEnd.startOf('day')) ? rawEnd : rawEnd.endOf('day');
    const s = Math.max(start.valueOf(), from.valueOf());
    const e = Math.min(Math.max(end.valueOf(), start.endOf('day').valueOf()), to.valueOf());
    return {
      left: `${((s - from.valueOf()) / span) * 100}%`,
      width: `${Math.max(0.8, ((e - s) / span) * 100)}%`,
    };
  };
  const days = Array.from({ length: DAYS }, (_, i) => from.add(i, 'day'));

  const onBlockClick = (b: ScheduleBlock) => {
    if (b.booking_item) return nav(`/bookings/${b.booking_item.booking.id}`);
    if (!canBlock) return;
    modal.confirm({
      title: `Gỡ khóa lịch (${BLOCK_STYLE[b.block_type].label})?`,
      content: `${dayjs(b.start_at).format('DD/MM HH:mm')} – ${dayjs(b.end_at).format('DD/MM HH:mm')}${b.note ? ` · ${b.note}` : ''}`,
      okText: 'Gỡ khóa',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await api.del(`/admin/availability-blocks/${b.id}`);
          void qc.invalidateQueries({ queryKey: ['schedule'] });
        } catch (e) {
          message.error(errorText(e));
        }
      },
    });
  };

  return (
    <Card
      title={
        <Typography.Title level={4} style={{ margin: 0 }}>
          Lịch xe
        </Typography.Title>
      }
      extra={
        <Space wrap>
          {Object.entries(BLOCK_STYLE).map(([k, v]) => (
            <Tag key={k} color={v.bg}>
              {v.label}
            </Tag>
          ))}
        </Space>
      }
    >
      <Space wrap style={{ marginBottom: 16 }}>
        <Button icon={<LeftOutlined />} onClick={() => setStart((s) => s.subtract(14, 'day'))} />
        <DatePicker
          value={start}
          format="DD/MM/YYYY"
          allowClear={false}
          onChange={(v) => v && setStart(v.startOf('day'))}
        />
        <Button icon={<RightOutlined />} onClick={() => setStart((s) => s.add(14, 'day'))} />
        <Button onClick={() => setStart(dayjs().startOf('day'))}>Hôm nay</Button>
        <Select
          allowClear
          showSearch
          optionFilterProp="label"
          placeholder="Tất cả sản phẩm"
          style={{ width: 260 }}
          value={productId}
          onChange={setProductId}
          options={(products.data ?? []).map((p) => ({ value: p.id, label: p.name }))}
        />
      </Space>

      {q.isLoading ? (
        <Spin />
      ) : !q.data?.equipment.length ? (
        <Empty description="Chưa có xe thực tế. Thêm xe ở mục Thiết bị thực tế." />
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <div style={{ minWidth: 1100 }}>
            <div
              style={{
                display: 'flex',
                borderBottom: '1px solid #E5E7EB',
                fontSize: 11,
                color: '#737B86',
              }}
            >
              <div style={{ width: 220, flex: 'none' }} />
              <div style={{ flex: 1, display: 'flex' }}>
                {days.map((d) => (
                  <div
                    key={d.valueOf()}
                    style={{
                      flex: 1,
                      textAlign: 'center',
                      padding: '4px 0',
                      background: d.day() === 0 ? '#FFF8F3' : undefined,
                      fontWeight: d.isSame(dayjs(), 'day') ? 700 : 400,
                    }}
                  >
                    {d.format('DD')}
                    <br />
                    {d.format('MM')}
                  </div>
                ))}
              </div>
            </div>
            {q.data.equipment.map((e) => (
              <div
                key={e.id}
                style={{
                  display: 'flex',
                  borderBottom: '1px solid #F0F0F0',
                  minHeight: 40,
                  alignItems: 'center',
                }}
              >
                <div style={{ width: 220, flex: 'none', padding: '4px 8px' }}>
                  <Space size={4}>
                    <b>{e.equipment_code}</b>
                    {e.ownership_type === 'partner' && <Tag color="purple">ĐT</Tag>}
                    {e.operational_status !== 'active' && (
                      <Tag color="orange">
                        {e.operational_status === 'maintenance' ? 'Bảo trì' : 'Ngưng'}
                      </Tag>
                    )}
                    {canBlock && (
                      <Tooltip title="Khóa lịch">
                        <Button
                          size="small"
                          type="text"
                          icon={<PlusOutlined />}
                          onClick={() => (
                            setAdding({ id: e.id, code: e.equipment_code }),
                            setForm({ range: null, type: 'maintenance', note: '' })
                          )}
                        />
                      </Tooltip>
                    )}
                  </Space>
                  <div style={{ fontSize: 11, color: '#737B86' }}>
                    {e.product.name}
                    {e.location ? ` · ${e.location.name}` : ''}
                  </div>
                </div>
                <div
                  style={{
                    flex: 1,
                    position: 'relative',
                    height: 28,
                    backgroundImage: `repeating-linear-gradient(to right, transparent 0, transparent calc(100%/${DAYS} - 1px), #F3F4F6 calc(100%/${DAYS} - 1px), #F3F4F6 calc(100%/${DAYS}))`,
                  }}
                >
                  {e.blocks.map((b) => (
                    <Tooltip
                      key={b.id}
                      title={
                        <>
                          <div>
                            {BLOCK_STYLE[b.block_type].label}
                            {b.booking_item
                              ? ` ${b.booking_item.booking.booking_code} · ${BOOKING_STATUS_LABEL[b.booking_item.booking.status]}`
                              : ''}
                          </div>
                          {b.booking_item && (
                            <div>
                              {b.booking_item.booking.customer.company_name ??
                                b.booking_item.booking.customer.full_name}
                            </div>
                          )}
                          <div>
                            {dayjs(b.start_at).format('DD/MM/YYYY')} –{' '}
                            {dayjs(b.end_at).format('DD/MM/YYYY')}
                          </div>
                          {b.note && <div>{b.note}</div>}
                        </>
                      }
                    >
                      <div
                        onClick={() => onBlockClick(b)}
                        style={{
                          position: 'absolute',
                          top: 4,
                          height: 20,
                          ...pos(b),
                          background: BLOCK_STYLE[b.block_type].bg,
                          borderRadius: 6,
                          color: '#fff',
                          fontSize: 11,
                          padding: '2px 6px',
                          overflow: 'hidden',
                          whiteSpace: 'nowrap',
                          textOverflow: 'ellipsis',
                          cursor: 'pointer',
                        }}
                      >
                        {b.booking_item
                          ? b.booking_item.booking.booking_code
                          : BLOCK_STYLE[b.block_type].label}
                      </div>
                    </Tooltip>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <Modal
        open={!!adding}
        title={`Khóa lịch xe ${adding?.code ?? ''}`}
        okText="Khóa lịch"
        okButtonProps={{ disabled: !form.range }}
        onCancel={() => setAdding(null)}
        onOk={async () => {
          if (!adding || !form.range) return;
          try {
            await api.post(`/admin/equipment/${adding.id}/availability-blocks`, {
              start_at: form.range[0].startOf('day').toISOString(),
              end_at: form.range[1].endOf('day').toISOString(),
              block_type: form.type,
              note: form.note.trim() || null,
            });
            message.success('Đã khóa lịch');
            setAdding(null);
            void qc.invalidateQueries({ queryKey: ['schedule'] });
          } catch (e) {
            message.error(errorText(e));
          }
        }}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <DatePicker.RangePicker
            format="DD/MM/YYYY"
            style={{ width: '100%' }}
            value={form.range}
            onChange={(v) => setForm((f) => ({ ...f, range: v as [Dayjs, Dayjs] | null }))}
          />
          <Select
            style={{ width: '100%' }}
            value={form.type}
            onChange={(v) => setForm((f) => ({ ...f, type: v }))}
            options={[
              { value: 'maintenance', label: 'Bảo trì / sửa chữa' },
              { value: 'reserved', label: 'Giữ chỗ (chưa có đơn)' },
              { value: 'manual', label: 'Khóa khác' },
            ]}
          />
          <Input.TextArea
            rows={2}
            placeholder="Ghi chú"
            value={form.note}
            onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
          />
        </Space>
      </Modal>
    </Card>
  );
}
